import {useSyncExternalStore} from "react";
import PeriodService from "../service/PeriodService";
import {store} from "../app/store";
import {setSchoolYear} from "../features/user/userSlice";
import {hasActiveSession, logoutExpiredSession} from "./axios_helpers";

/**
 * Background loader for the current school year (`/corebase/annee`).
 *
 * This used to be a hard gate: nothing in the protected app was rendered until
 * the request resolved. That serialized the whole boot sequence
 * (`main.js` → `/corebase/annee` → layout chunk → page chunk → page data), so a
 * slow year endpoint delayed *every* page even though only the school-period
 * config screen actually needs the value.
 *
 * It now runs entirely in the background: the app shell and the routed page
 * render immediately, and only the screens that read `user.schoolYear` wait on
 * the status exposed here.
 */

export type SchoolYearStatus = 'idle' | 'loading' | 'ready' | 'error';

let status: SchoolYearStatus = 'idle';
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const emit = (): void => {
    listeners.forEach((listener) => listener());
};

const setStatus = (next: SchoolYearStatus): void => {
    if (status === next) {
        return;
    }
    status = next;
    emit();
};

/** True when the school year is already available in the redux store. */
const hasSchoolYear = (): boolean => store.getState()?.user?.schoolYear != null;

const isSessionInvalid = (error: any): boolean => {
    if (error?.response?.status === 401) {
        return true;
    }
    return !hasActiveSession();
};

/**
 * Fetches the current school year once (deduping concurrent/duplicate callers)
 * and stores it in redux. Never rejects: failures are reported through the
 * status, and the axios interceptor already surfaces backend/network errors.
 *
 * @param force re-fetch even when a value is already in the store
 */
export const ensureSchoolYearLoaded = (force = false): Promise<void> => {
    if (!force && hasSchoolYear()) {
        setStatus('ready');
        return Promise.resolve();
    }
    if (inFlight != null) {
        return inFlight;
    }

    setStatus('loading');
    inFlight = PeriodService().getSchoolPeriod()
        .then((period: any) => {
            const schoolResult: any[] = period?._embedded?.anneeDTOModelList ?? [];
            // Resolve even when no year exists yet (page handles the null).
            if (schoolResult.length > 0) {
                store.dispatch(setSchoolYear(schoolResult[0]));
            }
            setStatus('ready');
        })
        .catch((error: any) => {
            // A dead session must not leave the user on a broken screen: clear it
            // and let the login page explain the logout (same path as any other
            // 401 in the app).
            if (isSessionInvalid(error)) {
                setStatus('error');
                logoutExpiredSession();
                return;
            }
            setStatus('error');
        })
        .finally(() => {
            inFlight = null;
            emit();
        });

    return inFlight;
};

export const getSchoolYearStatus = (): SchoolYearStatus => {
    // The value may have been restored from persistence before any fetch ran.
    if (status === 'idle' && hasSchoolYear()) {
        return 'ready';
    }
    return status;
};

export const subscribeSchoolYearStatus = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * Reactive status for screens that truly need the school year. The returned
 * value is 'loading' before the first fetch and after a successful one only
 * when the value is actually present.
 */
export const useSchoolYearStatus = (): SchoolYearStatus => {
    return useSyncExternalStore(subscribeSchoolYearStatus, getSchoolYearStatus, getSchoolYearStatus);
};
